import _ from 'lodash';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Collapse,
    IconButton,
    IconButtonProps,
    Link,
    Tooltip
} from '@mui/material';
import { getIcon } from '../index';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { ArrowDropDownOutlined as ArrowDropDownOutlinedIcon } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Remove';
import styles from '../styles.menu.module.css';
import { useRoles } from '../../../hooks/useRoles';

const CardComponent = styled(Card)`
    cursor: pointer;
    box-shadow: none;
    margin: 12px 0px;
    background-color: transparent;
    font-weight: 500;
    color: var(--white);
    font-size: 18px;
    button {
        margin-left: inherit;
        svg {
            color: white;
        }
    }
    & .MuiCardContent-root:last-child {
        padding-bottom: 0;
    }
    & .MuiCollapse-root {
        & .MuiTypography-root {
            color: var(--white);
            text-transform: capitalize;
            margin: 10px 0;
        }
    }
`;

const hoverStyles = () => ({
    '& .MuiCardActions-root:hover': {
        color: 'white',
        fontWeight: '600',
        textDecoration: 'none',
        padding: '8px',
        borderRadius: '4px',
        '& button': {
            background: '#00000075'
        },
        '& button svg': {
            color: '#fff'
        },
        '& span:hover': {
            fontWeight: '600'
        }
    },
    '& .MuiLink-root:hover': {
        color: 'white',
        fontWeight: '600',
        textDecoration: 'none',
        padding: '16px',
        borderRadius: '4px',
        background: (theme:any) => theme.palette.primary.dark
    }
});

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest
    })
}));

function MenuAccessItem({ item, sideMenu, handleDrawerToggle, open, active = false }: any) {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const { isAdminBO, isSupervisorSIMP, isOperatorSIMP, isViewerBO, isRRHHSIMP, isPreventor } =
        useRoles();
    const location = useLocation();
    const checkRoutePrefix = () => {
        if (isAdminBO || isViewerBO || isPreventor) {
            return 'backoffice';
        }

        if (isSupervisorSIMP || isOperatorSIMP || isRRHHSIMP) {
            return 'simp';
        }

        return 'prefixError';
    };

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const textTransform = (value: string) =>
        value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

    useEffect(() => {
        if (sideMenu && !open && expanded) {
            handleExpandClick();
        }
        // eslint-disable-next-line
    }, [open, expanded, sideMenu]);

    const functionsCount = (fns: any) => {
        const initialValue = 0;
        const value = fns?.reduce(
            (total: any, currentValue: any) => total + currentValue?.visualizarMenu,
            initialValue
        );

        return value;
    };

    const getExpandIcons = () => {
        if (sideMenu) {
            return <ArrowDropDownOutlinedIcon />;
        } else {
            return expanded ? <RemoveIcon /> : <AddIcon />;
        }
    };

    const getPath = () =>
        item?.funcionalidades?.filter((i: any) => i.visualizarMenu)[0]?.urlRedireccion;
    if (!item?.funcionalidades?.some((i: any) => i?.visualizarMenu)) return null;

    const getActiveSubFunctionIcon = () => {
        if (location.pathname === '/usuarios/perfil') return null;

        const activeFn = adjustedFuncionalidades.find((fn: any) => {
            const path = fn.urlRedireccion.startsWith('/')
                ? fn.urlRedireccion
                : '/' + fn.urlRedireccion;

            return location.pathname === path || location.pathname.startsWith(path + '/');
        });

        return activeFn ? getIcon(activeFn.id, isSupervisorSIMP ?? false) : null;
    };

    const adjustedFuncionalidades = item?.funcionalidades?.map((func: any) => {
        if ((isSupervisorSIMP || isOperatorSIMP) && func.id === 14) {
            return { ...func, visualizarMenu: false };
        }

        if (isPreventor && (func.id === 14 || func.id === 9)) {
            return { ...func, visualizarMenu: false };
        }

        return func;
    });

    if (!adjustedFuncionalidades?.some((i: any) => i?.visualizarMenu)) return null;

    return (
        <CardComponent sx={{ ...hoverStyles() }}>
            <CardActions
                onClick={() => {
                    if (functionsCount(item?.funcionalidades) > 1) {
                        handleExpandClick();
                        if (!open && handleDrawerToggle) handleDrawerToggle();
                    } else {
                        navigate(`${checkRoutePrefix()}${getPath()}`);
                        if (open && handleDrawerToggle) handleDrawerToggle();
                    }
                }}
                className={styles[`${sideMenu ? 'grouper-header-side' : 'grouper-header'}`]}
            >
                <Box>
                    {sideMenu && !open ? (
                        <Tooltip title={textTransform(item?.nombre)} placement="right">
                            {(() => {
                                const subIcon =
                                    sideMenu && !open ? getActiveSubFunctionIcon() : null;
                                return (
                                    <IconButton
                                        sx={{
                                            backgroundColor: (theme) =>
                                                active ? theme.palette.action.active : 'default',
                                            borderRadius: '8px',
                                            width: 40,
                                            height: subIcon ? 82 : 40,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: subIcon ? 2 : 0,
                                            '& svg': {
                                                fontSize: 22
                                            }
                                        }}
                                    >
                                        {getIcon(item?.id, isSupervisorSIMP ?? false)}
                                        {subIcon}
                                    </IconButton>
                                );
                            })()}
                        </Tooltip>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px'
                            }}
                        >
                            {getIcon(item?.id, isSupervisorSIMP ?? false)}
                            {!open && sideMenu && getActiveSubFunctionIcon()}
                        </Box>
                    )}

                    <span>{item?.nombre}</span>
                </Box>
                {functionsCount(item?.funcionalidades) > 1 && (
                    <ExpandMore expand={expanded} aria-expanded={expanded} aria-label="show more">
                        {getExpandIcons()}
                    </ExpandMore>
                )}
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ p: '0 16px' }}>
                    {item?.funcionalidades?.map((fn: any, index: number) => {
                        if (fn.visualizarMenu) {
                            return (
                                <Link
                                    className={styles['collapsed-items-menu']}
                                    onClick={() => {
                                        navigate(`${fn.urlRedireccion}`);
                                        sideMenu && handleDrawerToggle();
                                    }}
                                    key={index}
                                    component="div"
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getIcon(fn.id, !!isSupervisorSIMP)}
                                        <span>{fn.nombre}</span>
                                    </Box>
                                </Link>
                            );
                        } else return null;
                    })}
                </CardContent>
            </Collapse>
        </CardComponent>
    );
}

export default MenuAccessItem;
